import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

export default function AttendanceModal({ classData, onClose, onSaved }) {
  useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [existingAttendance, setExistingAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [alertMemberId, setAlertMemberId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!classData?.id) return;
    setLoading(true);
    try {
      const [enrollResponse, attendResponse] = await Promise.all([
        api.get(`/classes/${classData?.id}/enrollments`),
        api.get('/attendances', { params: { class_id: classData?.id } }),
      ]);

      const enrollList = enrollResponse?.data || [];
      setEnrollments(enrollList);

      const existingMap = {};
      (attendResponse?.data || [])?.forEach(a => {
        existingMap[a?.member_id] = a;
      });
      setExistingAttendance(existingMap);

      // Pre-mark: present if already checked in, else absent
      const initialAttendance = {};
      enrollList?.forEach(e => {
        initialAttendance[e?.member_id] = existingMap?.[e?.member_id] ? "present" : "absent";
      });
      setAttendance(initialAttendance);
    } catch (err) {
      setError(err?.message || "Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  }, [classData?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = (memberId) => {
    // If already checked in, block and show alert
    if (existingAttendance?.[memberId]) {
      setAlertMemberId(memberId);
      setTimeout(() => setAlertMemberId(null), 3000);
      return;
    }
    // Toggle only for not-yet-checked-in members
    setAttendance(prev => ({
      ...prev,
      [memberId]: prev?.[memberId] === "present" ? "absent" : "present",
    }));
  };

  const markAllPresent = () => {
    const all = {};
    enrollments?.forEach(e => { all[e?.member_id] = "present"; });
    setAttendance(all);
  };

  const presentCount = Object.values(attendance)?.filter(v => v === "present")?.length;

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const now = new Date()?.toISOString();
      const classDate = classData?.start_date;
      const classTime = classData?.start_time;
      const checkInTime = classDate && classTime
        ? new Date(`${classDate}T${classTime}`)?.toISOString()
        : now;

      // Only insert NEW check-ins (never delete existing ones)
      const insertPromises = enrollments
        ?.filter(enrollment => {
          const isPresent = attendance?.[enrollment?.member_id] === "present";
          const alreadyCheckedIn = !!existingAttendance?.[enrollment?.member_id];
          return isPresent && !alreadyCheckedIn;
        })
        ?.map(enrollment =>
          api.post('/attendances', {
            member_id: enrollment?.member_id,
            class_id: classData?.id,
            check_in_time: checkInTime,
            check_in_type: "class",
          })
        );

      await Promise.all(insertPromises);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Failed to save attendance");
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (timeStr) => timeStr?.slice(0, 5) || "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center">
                <Icon name="ClipboardCheck" size={14} color="#16a34a" />
              </div>
              <h2 className="text-base font-bold text-[var(--color-foreground)]">Mark Attendance</h2>
            </div>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {classData?.name} · {formatTime(classData?.start_time)} – {formatTime(classData?.end_time)} · {classData?.start_date}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Summary Bar */}
        <div className="flex items-center justify-between px-5 py-3 bg-[var(--color-muted)]/30 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <span className="text-sm font-medium text-[var(--color-foreground)]">{presentCount} Present</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-muted-foreground)]" />
              <span className="text-sm text-[var(--color-muted-foreground)]">{enrollments?.length - presentCount} Absent</span>
            </div>
          </div>
          <button
            onClick={markAllPresent}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
          >
            <Icon name="CheckCheck" size={13} color="#15803d" />
            Mark All Present
          </button>
        </div>

        {/* Member List */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-3">
              <Icon name="AlertCircle" size={14} color="#ef4444" />
              {error}
            </div>
          )}

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4]?.map(i => (
                <div key={i} className="h-14 bg-[var(--color-muted)] rounded-lg animate-pulse" />
              ))}
            </div>
          ) : enrollments?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-3">
                <Icon name="Users" size={20} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-sm font-medium text-[var(--color-foreground)]">No enrolled members</p>
              <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Enroll members first to track attendance</p>
            </div>
          ) : (
            <div className="space-y-2">
              {enrollments?.map(enrollment => {
                const isPresent = attendance?.[enrollment?.member_id] === "present";
                const alreadyCheckedIn = !!existingAttendance?.[enrollment?.member_id];
                const showingAlert = alertMemberId === enrollment?.member_id;
                return (
                  <div key={enrollment?.id}>
                    {showingAlert && (
                      <div className="flex items-center gap-2 px-3 py-2 mb-1 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                        <Icon name="AlertCircle" size={13} color="#d97706" />
                        Already checked in today.
                      </div>
                    )}
                    <div
                      onClick={() => handleToggle(enrollment?.member_id)}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                        alreadyCheckedIn
                          ? "bg-green-50 border-green-200 cursor-not-allowed opacity-80"
                          : isPresent
                          ? "bg-green-50 border-green-200 hover:bg-green-100 cursor-pointer" :"bg-[var(--color-muted)]/40 border-[var(--color-border)] hover:border-[var(--color-primary)]/30 cursor-pointer"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isPresent ? "bg-green-500" : "bg-[var(--color-muted)]"}`}>
                          {isPresent ? (
                            <Icon name="Check" size={14} color="white" />
                          ) : (
                            <span className="text-xs font-bold text-[var(--color-muted-foreground)]">
                              {enrollment?.member_name?.split(' ')?.[0]?.[0]}{enrollment?.member_name?.split(' ')?.[1]?.[0]}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[var(--color-foreground)]">
                            {enrollment?.member_name || "Unknown"}
                          </p>
                          <p className="text-xs text-[var(--color-muted-foreground)]">
                            {enrollment?.phone || "No contact"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {alreadyCheckedIn && (
                          <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full border border-green-200">
                            <Icon name="Lock" size={10} color="#15803d" />
                            Checked In
                          </span>
                        )}
                        {!alreadyCheckedIn && (
                          <span className={`text-xs font-medium px-2 py-1 rounded-md ${isPresent ? "bg-green-100 text-green-700" : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"}`}>
                            {isPresent ? "Present" : "Absent"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || enrollments?.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" size={14} color="white" />
                Save Check-ins
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
